import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  ServerError,
  TypePolicies,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { relayStylePagination } from "@apollo/client/utilities";
import { useAuth } from "~/login/provider";
import { AuthContextTypes } from "~/login/auth-context";
import * as Sentry from "@sentry/react";
import { nexusStylePagination } from "~/lib/nexusStylePagination";
import { useRegions } from "./RegionsProvider";
import { FeatureFlag } from "~/login/features";

export type GraphqlContextTypes =
  | { client: ApolloClient<NormalizedCacheObject> }
  | undefined;

export const GraphqlContext = createContext<GraphqlContextTypes>(undefined);

export type GraphqlProviderProps = { children: ReactNode };

export function GraphqlProvider({ children }: GraphqlProviderProps) {
  const auth = useAuth();
  const { selectedGraphqlEndpoint } = useRegions();
  const [client, setClient] = useState<
    ApolloClient<NormalizedCacheObject> | undefined
  >(undefined);

  useEffect(() => {
    (async () => {
      const { user } = auth.state;
      if (user) {
        const token = await user.getIdToken(false);
        const refreshToken = () => user.getIdToken(true);
        const client = initApolloClient({
          token,
          refreshToken,
          auth,
          selectedGraphqlEndpoint,
        });
        setClient(client);
      }
    })();
  }, []);

  return (
    <GraphqlContext.Provider value={client ? { client } : undefined}>
      {client && <ApolloProvider client={client}>{children}</ApolloProvider>}
    </GraphqlContext.Provider>
  );
}

export function useGraphql() {
  const context = useContext(GraphqlContext);
  if (context === undefined) {
    throw new Error("useGraphql must be used within a GraphqlProvider");
  }
  return context;
}

export type GraphqlOptions = {
  token: string;
  refreshToken: () => Promise<string>;
  auth: AuthContextTypes;
  selectedGraphqlEndpoint: string;
};

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      activePolicies: relayStylePagination([
        "input",
        ["scopeMrn", "orderBy", "query"],
      ]),
      advisories: relayStylePagination(["orderBy", "query", "platform"]),
      listDocuments: relayStylePagination(["scopeMRN"]),
      auditlog: relayStylePagination([
        "resourceMrn",
        "orderBy",
        "actionFilter",
        "identityFilter",
        "timestampFilter",
      ]),
      assets: relayStylePagination([
        "scopeMrn",
        "queryTerms",
        "platformKind",
        "platformName",
        "rating",
        "reboot",
        "labels",
        "groups",
        "assetTypes",
        "scoreRange",
        "workflowMrn",
      ]),
      assetSearch: relayStylePagination(["input", "orderBy"]),
      aggregateScores: nexusStylePagination(["entityMrn", "filter", "orderBy"]),
      complianceFramework: {
        merge: true,
      },
      content: relayStylePagination([
        "input",
        [
          "assignedOnly",
          "catalogType",
          "categories",
          "contentMrns",
          "includePrivate",
          "includePublic",
          "orderBy",
          "platforms",
          "query",
          "scopeMrn",
        ],
      ]),
      cves: relayStylePagination(["orderBy", "query", "state"]),
      dataQueries: relayStylePagination(["entityMrn", "orderBy", "filter"]),
      serviceAccounts: relayStylePagination(["spaceMrn", "queryTerms"]),
      agents: relayStylePagination([
        "spaceMrn",
        "queryTerms",
        "version",
        "state",
      ]),
      registrationTokens: relayStylePagination(["spaceMrn"]),
      mqueryAssetScores: relayStylePagination(["input"]),
      search: relayStylePagination([
        "scope",
        "query",
        "orderBy",
        "type",
        "filters",
      ]),
      vulnerabilityScores: relayStylePagination([
        "orderBy",
        "filter",
        "entityMrn",
      ]),
      checkScores: relayStylePagination(["orderBy", "filter", "entityMrn"]),
      findings: nexusStylePagination(["orderBy", "filter", "scopeMrn"]),
      cases: nexusStylePagination(["input"]),
      listExceptionGroups: nexusStylePagination([
        "input",
        ["scopeMrn", "types", "includeChildScopes", "filter", "orderBy", "mrn"],
      ]),
      remediationForScope: relayStylePagination([
        "vulnId",
        "scopeMrn",
        "ecosystem",
        "packages",
      ]),
      sharedSpaces: relayStylePagination(),
    },
  },
  Cve: {
    keyFields: ["id"],
    fields: {
      advisoryAggregateScores: relayStylePagination(["id", "scopeMrn"]),
    },
  },
  DataQuery: {
    keyFields: ["id", "mrn"],
  },
  Evidence: {
    keyFields: ["id", "vexMrn"],
  },
  Case: {
    keyFields: ["mrn"],
    fields: {
      affectedAssets: relayStylePagination(["mrn"]),
      mitigated: relayStylePagination(["mrn"]),
    },
  },
  VulnerabilityScore: {
    keyFields: ["mrn", "asset"],
  },
  CheckScore: {
    keyFields: ["mrn", "asset"],
  },
  CheckFinding: {
    keyFields: ["mrn", "asset"],
  },
  AdvisoryFinding: {
    keyFields: ["mrn", "asset"],
  },
  CveFinding: {
    keyFields: ["mrn", "asset"],
  },
  GenericFinding: {
    keyFields: ["mrn", "asset"],
  },
  PackageFinding: {
    keyFields: ["id", "asset"],
  },
  AggregateScore: {
    keyFields: false,
  },
  ComplianceControl: {
    keyFields: ["mrn"],
    merge: true,
    fields: {
      checks: relayStylePagination(),
      dataQueries: relayStylePagination(),
    },
  },
  ServiceAccount: {
    keyFields: ["mrn"],
  },
  RegistrationToken: {
    keyFields: ["mrn"],
  },
  // We may want to remove this and just let graphql infer a key
  Integration: {
    keyFields: ["mrn", "name"],
  },
  Space: {
    keyFields: ["mrn"],
    fields: {
      members: relayStylePagination(["orderBy", "query"]),
      stats: {
        merge: true,
      },
    },
  },
  Workspace: {
    keyFields: ["mrn"],
  },
  Organization: {
    keyFields: ["mrn"],
    fields: {
      members: relayStylePagination([
        "orderBy",
        "query",
        "rolesFilter",
        "identityTypeFilter",
      ]),
      spacesList: relayStylePagination(["mrn", "name"]),
    },
  },
  User: {
    keyFields: ["mrn"],
  },
  ProductInfo: {
    keyFields: ["id", "name"],
  },
  Asset: {
    keyFields: ["mrn"],
    fields: {
      platform: {
        merge: true,
      },
      report: {
        merge: true,
      },
      score: {
        merge: false,
      },
    },
  },
  MqueryRemediation: {
    keyFields: ["id", "desc"],
  },
  CicdProjectJobs: {
    fields: {
      jobs: relayStylePagination(),
    },
  },
  Report: {
    fields: {
      cves: relayStylePagination(["assetMrn"]),
      packages: relayStylePagination(["assetMrn"]),
      advisories: relayStylePagination(["assetMrn"]),
      stats: {
        merge: true,
      },
    },
  },
  Agent: {
    keyFields: ["mrn"],
  },
  Invitation: {
    keyFields: ["mrn"],
  },
  Policy: {
    keyFields: false,
    fields: {
      queries: {
        merge: false,
      },
    },
  },
  PolicyReport: {
    keyFields: ["mrn"],
  },
  Score: {
    keyFields: false,
  },
  MvdSource: {
    keyFields: false,
  },
  WIFAuthBinding: {
    keyFields: ["mrn"],
  },
};

function initApolloClient({
  token,
  refreshToken,
  auth,
  selectedGraphqlEndpoint,
}: GraphqlOptions) {
  const httpLink = new HttpLink({
    uri: selectedGraphqlEndpoint,
  });

  // Some Console feature flags are tied to Nexus feature flags via HTTP header
  const featureLink = setContext((_, { headers }) => {
    const storedFeatures = (() => {
      try {
        return JSON.parse(
          localStorage.getItem("features") ?? "[]",
        ) as FeatureFlag[];
      } catch (error) {
        return [];
      }
    })();
    const headerKey = "X-MONDOO-FEATURE-FLAGS";
    const headerValue = buildFullFeatureFlagHeader(storedFeatures);

    return {
      headers: {
        ...headers,
        [headerKey]: headerValue,
      },
    };
  });

  const crumbLink = new ApolloLink((operation, forward) => {
    if (!Sentry.isInitialized()) return forward(operation);

    const crumbStart = Date.now();

    return forward(operation).map((resp) => {
      const crumbEnd = Date.now();
      const duration = crumbEnd - crumbStart;
      const { data, errors } = resp;
      const hasErrors = Boolean(errors) || !Boolean(data);
      const level = hasErrors ? "error" : "info";
      const status = hasErrors ? "error" : "success";

      Sentry.addBreadcrumb({
        type: "http",
        category: `graphql.${status}`,
        level,
        message: `Operation ${operation.operationName} finish [${duration}ms]`,
        data: hasErrors ? { errors } : undefined,
      });

      return resp;
    });
  });

  let bearerToken = token;

  const retryLink = new RetryLink({
    delay: {
      initial: 0,
    },
    attempts: {
      max: 2,
      retryIf: (error) => {
        if (error && (error as ServerError).statusCode !== 401) {
          return false;
        }

        return new Promise((resolve, reject) => {
          if (!refreshToken) {
            return reject(
              "graphql> cannot get the global user token refresh function",
            );
          }

          return refreshToken()
            .then((token) => {
              bearerToken = token;
              resolve(true);
            })
            .catch(() => {
              resolve(false);
            });
        });
      },
    },
  });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        Authorization: bearerToken != null ? `Bearer ${bearerToken}` : "",
      },
    };
  });

  const errorLink = onError(({ networkError, operation }) => {
    if (
      networkError &&
      "statusCode" in networkError &&
      networkError.statusCode === 422
    ) {
      Sentry.captureException(
        new Error(`Unprocessable Entity: 422 - ${operation.operationName}`),
        {
          contexts: {
            Response: {
              statusCode: networkError.statusCode,
              result:
                "result" in networkError
                  ? JSON.stringify(networkError.result)
                  : null,
            },
            Graphql: {
              query: operation?.query.loc?.source.body,
              variables: JSON.stringify(operation?.variables),
            },
          },
        },
      );
    }

    if (networkError && (networkError as ServerError).statusCode === 401) {
      // retryLink attempts have failed if 401 has reached errorLink
      auth?.logout();
    }

    if (networkError && (networkError as ServerError).statusCode === 451) {
      auth?.setGeolock(true);
    }
  });

  // Add extra tracing metadata to request headers
  const traceLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        "X-Console-Url": window.location.pathname,
      },
    };
  });

  const cache = new InMemoryCache({
    typePolicies,
  });

  const client = new ApolloClient({
    link: from([
      errorLink,
      retryLink,
      authLink,
      traceLink,
      crumbLink,
      featureLink,
      httpLink,
    ]),
    cache,
    devtools: {
      enabled: true,
    },
    assumeImmutableResults: false,
  });

  return client;
}

/**
 * Builds the value for the X-MONDOO-FEATURE-FLAGS header from stored feature flags.
 * Only feature flags with a `headerKey` should produce an entry.
 */
export function buildFeatureFlagHeaderValue(features: FeatureFlag[]): string {
  return features
    .flatMap(({ headerKey, enabled }) =>
      headerKey !== undefined
        ? `${headerKey}=${enabled ? "enabled" : "disabled"}`
        : [],
    )
    .join(";");
}

/**
 * Builds the full feature flags header value by combining feature flag entries
 * with the shared space header (org_members=enabled), ensuring no duplicate keys.
 */
export function buildFullFeatureFlagHeader(features: FeatureFlag[]): string {
  const featureFlagsHeaderValue = buildFeatureFlagHeaderValue(features);
  const sharedSpaceHeader = "org_members=enabled";

  // If org_members is already present from feature flags, don't add it again
  if (featureFlagsHeaderValue.includes("org_members=")) {
    return featureFlagsHeaderValue;
  }

  return featureFlagsHeaderValue.length > 0
    ? `${featureFlagsHeaderValue};${sharedSpaceHeader}`
    : sharedSpaceHeader;
}
